/**
 * Modale creazione fattura fornitore.
 * Riga per riga, con collegamento opzionale a subcontract_item.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { X, Plus, Trash2 } from 'lucide-react';
import { fmtEUR } from '../../lib/contractModificationApi';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem('token');
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

const TAX_REGIMES = [
  { v: 'iva_ordinaria', l: 'IVA Ordinaria' },
  { v: 'reverse_charge_edilizio', l: 'Reverse Charge Edilizio' },
  { v: 'split_payment', l: 'Split Payment' },
  { v: 'fuori_campo', l: 'Fuori Campo' },
  { v: 'esente', l: 'Esente' },
  { v: 'non_soggetto', l: 'Non Soggetto' },
];

const SupplierInvoiceCreateModal = ({ subcontract, onClose, onCreated }) => {
  const [items, setItems] = useState([]);
  const [sals, setSals] = useState([]);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    invoice_number: '',
    invoice_date: new Date().toISOString().slice(0, 10),
    received_date: new Date().toISOString().slice(0, 10),
    due_date: '',
    tax_regime: 'iva_ordinaria',
    vat_rate: 22,
    subcontract_sal_id: '',
    notes: '',
  });

  const [lines, setLines] = useState([
    { description: '', quantity: 1, unit_price: 0, net_amount: 0, subcontract_item_id: '' },
  ]);

  // Load subcontract items + SALs
  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/api/subcontracts/${subcontract.id}/items`, auth()).catch(() => ({ data: [] })),
          axios.get(`${API}/api/subcontracts/${subcontract.id}/sals`, auth()).catch(() => ({ data: { sals: [] } })),
        ]);
        setItems(Array.isArray(r1.data) ? r1.data : (r1.data.items || []));
        setSals(r2.data?.sals || r2.data || []);
      } catch (err) { /* silent fallback: uses empty defaults */ }
    })();
  }, [subcontract?.id]);

  const updateLine = (idx, key, val) => {
    setLines((prev) => prev.map((ln, i) => {
      if (i !== idx) return ln;
      const upd = { ...ln, [key]: val };
      if (key === 'quantity' || key === 'unit_price') {
        upd.net_amount = Number(((Number(upd.quantity) || 0) * (Number(upd.unit_price) || 0)).toFixed(2));
      }
      return upd;
    }));
  };

  const addLine = () => setLines((p) => [...p, { description: '', quantity: 1, unit_price: 0, net_amount: 0, subcontract_item_id: '' }]);
  const removeLine = (i) => setLines((p) => p.filter((_, ii) => ii !== i));

  const subtotal = lines.reduce((s, l) => s + (Number(l.net_amount) || 0), 0);
  const isReverseOrOutOfScope = ['reverse_charge_edilizio', 'fuori_campo', 'esente', 'non_soggetto'].includes(form.tax_regime);
  const vatAmount = isReverseOrOutOfScope ? 0 : Number(((subtotal * (Number(form.vat_rate) || 0)) / 100).toFixed(2));
  const total = Number((subtotal + vatAmount).toFixed(2));
  // Split payment: fornitore riceve solo l'imponibile; PA versa l'IVA
  const netToPay = form.tax_regime === 'split_payment' ? subtotal : total;

  const submit = async () => {
    if (!form.invoice_number.trim()) return toast.error('Numero fattura obbligatorio');
    if (lines.length === 0 || lines.every((l) => !l.description.trim())) return toast.error('Almeno una riga con descrizione');

    const payload = {
      project_id: subcontract.project_id,
      subcontract_id: subcontract.id,
      subcontract_sal_id: form.subcontract_sal_id || null,
      supplier_id: subcontract.supplier_id,
      supplier_name: subcontract.supplier_snapshot?.ragione_sociale || null,
      invoice_number: form.invoice_number.trim(),
      invoice_date: form.invoice_date,
      received_date: form.received_date,
      due_date: form.due_date || null,
      tax_regime: form.tax_regime,
      vat_rate: Number(form.vat_rate) || 0,
      vat_amount: vatAmount,
      subtotal_taxable: Number(subtotal.toFixed(2)),
      total_document: total,
      net_to_pay: Number(netToPay.toFixed(2)),
      withholding_tax_rate: 0,
      withholding_tax_amount: 0,
      notes: form.notes || null,
      lines: lines
        .filter((l) => l.description.trim())
        .map((l) => ({
          description: l.description.trim(),
          quantity: Number(l.quantity) || 1,
          unit_price: Number(l.unit_price) || 0,
          net_amount: Number(l.net_amount) || 0,
          subcontract_item_id: l.subcontract_item_id || null,
          project_economic_line_id: null,
        })),
    };

    setSaving(true);
    try {
      await axios.post(`${API}/api/supplier-invoices`, payload, auth());
      toast.success('Fattura creata (stato: ricevuta)');
      onCreated?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Errore creazione');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-2 md:p-4" data-testid="invoice-create-modal">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-y-auto">
        <div className="p-4 md:p-5 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-lg font-semibold">Nuova fattura fornitore</h3>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="invoice-close">
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="p-4 md:p-5 space-y-4">
          {/* Testata */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <FormField label="Numero *">
              <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="invoice-number" />
            </FormField>
            <FormField label="Data fattura">
              <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="invoice-date" />
            </FormField>
            <FormField label="Data ricezione">
              <Input type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} data-testid="invoice-received" />
            </FormField>
            <FormField label="Scadenza">
              <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="invoice-due" />
            </FormField>
            <FormField label="Regime fiscale">
              <select
                value={form.tax_regime}
                onChange={(e) => setForm({ ...form, tax_regime: e.target.value })}
                className="border rounded px-2 py-1 text-sm w-full"
                data-testid="invoice-tax-regime"
              >
                {TAX_REGIMES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
            </FormField>
            <FormField label="Aliquota IVA %">
              <Input
                type="number" step="0.5" min="0" max="100"
                value={form.vat_rate}
                onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
                disabled={isReverseOrOutOfScope}
                data-testid="invoice-vat-rate"
              />
            </FormField>
            <FormField label="SAL collegato">
              <select
                value={form.subcontract_sal_id}
                onChange={(e) => setForm({ ...form, subcontract_sal_id: e.target.value })}
                className="border rounded px-2 py-1 text-sm w-full"
                data-testid="invoice-sal"
              >
                <option value="">— nessuno —</option>
                {(sals || []).map((s) => (
                  <option key={s.id} value={s.id}>SAL {s.progressive || s.number || s.id.slice(0, 6)}</option>
                ))}
              </select>
            </FormField>
            <FormField label="Note">
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="invoice-notes" />
            </FormField>
          </div>

          {/* Righe */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-medium">Righe fattura</div>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="invoice-add-line">
                <Plus className="w-3 h-3 mr-1" /> Riga
              </Button>
            </div>
            <div className="space-y-2">
              {lines.map((ln, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-start" data-testid={`invoice-line-${i}`}>
                  <div className="col-span-4">
                    <Input placeholder="Descrizione" value={ln.description} onChange={(e) => updateLine(i, 'description', e.target.value)} />
                  </div>
                  <div className="col-span-1">
                    <Input type="number" step="0.01" placeholder="Q.tà" value={ln.quantity} onChange={(e) => updateLine(i, 'quantity', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" step="0.01" placeholder="Prezzo" value={ln.unit_price} onChange={(e) => updateLine(i, 'unit_price', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" step="0.01" placeholder="Netto" value={ln.net_amount} onChange={(e) => updateLine(i, 'net_amount', e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <select value={ln.subcontract_item_id} onChange={(e) => updateLine(i, 'subcontract_item_id', e.target.value)} className="border rounded px-1 py-1 text-xs w-full">
                      <option value="">— link item —</option>
                      {items.map((it) => (
                        <option key={it.id} value={it.id}>{(it.original_description || '').slice(0, 40)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-1 text-right">
                    <Button variant="ghost" size="sm" onClick={() => removeLine(i)} disabled={lines.length === 1}>
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Totali */}
          <div className="rounded-md bg-slate-50 border p-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><div className="text-[11px] uppercase text-slate-500">Imponibile</div><div className="font-semibold">{fmtEUR(subtotal)}</div></div>
            <div><div className="text-[11px] uppercase text-slate-500">IVA</div><div className="font-semibold">{fmtEUR(vatAmount)}</div></div>
            <div><div className="text-[11px] uppercase text-slate-500">Totale doc.</div><div className="font-semibold">{fmtEUR(total)}</div></div>
            <div><div className="text-[11px] uppercase text-slate-500">Netto da pagare</div><div className="font-semibold text-blue-700" data-testid="invoice-net-preview">{fmtEUR(netToPay)}</div></div>
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-2 sticky bottom-0 bg-white">
          <Button variant="ghost" onClick={onClose}>Annulla</Button>
          <Button onClick={submit} disabled={saving} data-testid="invoice-submit">
            {saving ? 'Salvataggio…' : 'Crea fattura'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const FormField = ({ label, children }) => (
  <div>
    <Label className="text-xs text-slate-500 mb-1 block">{label}</Label>
    {children}
  </div>
);

export default SupplierInvoiceCreateModal;
